import { Fragment, useCallback, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { MultiValue, SingleValue } from 'react-select';

import { Divider } from '@hsp/react-component-library';
import { isDefined } from '@hsp/utilities';

import { VirtualSelect } from 'components/select';
import { useFetchDistricts, useFetchStates } from 'hooks/queries';
import { ExcludeIncludeButtonGroup } from 'pages/audience/shared/exclude-include';
import { ISelectedDistrictFilter } from 'types/api/audience';
import { SearchType } from 'types/api/audience/enums';
import { ExcludeInclude } from 'types/api/common';
import { IDistrict } from 'types/api/districts';
import { IState } from 'types/api/states';
import { s } from 'utilities/strings';

const formName = SearchType.CongressionalDistricts;

const Congressional = () => {
  const [selectedState, setSelectedState] = useState<string | undefined>();

  const { data: states, isLoading: isStatesLoading } = useFetchStates();
  const { data: allDistricts } = useFetchDistricts();
  const { data: districts, isFetching: isDistrictsLoading } = useFetchDistricts(true, selectedState);
  const { getValues, setValue } = useFormContext();

  const getValue = useCallback(() => {
    const current = getValues(formName) as ISelectedDistrictFilter;

    return current;
  }, [getValues]);
  const getDistrictValue = useCallback(() => {
    const current = getValue();

    if (!isDefined(selectedState)) {
      return null;
    }

    const currentDistricts = current?.filters[selectedState]?.selected ?? [];

    return (districts ?? []).filter((district) =>
      currentDistricts.find((selected) => district.district === selected.district)
    );
  }, [districts, getValue, selectedState]);
  const getIncludeExclude = useCallback(() => {
    return getValue()?.type;
  }, [getValue]);
  const getStateValue = useCallback(() => {
    return isDefined(selectedState) ? (states ?? []).find(({ code }) => code === selectedState) ?? null : [];
  }, [selectedState, states]);
  const handleOnDistrictChanged = useCallback(
    (value: MultiValue<IDistrict>) => {
      const current = getValue();

      if (!isDefined(value) || !isDefined(selectedState)) {
        return;
      }

      setValue(`${formName}`, {
        ...current,
        filters: {
          ...current.filters,
          [selectedState]: {
            ...current.filters[selectedState],
            selected: value,
          },
        },
      });
    },
    [getValue, selectedState, setValue]
  );
  const handleOnExcludeIncludeClick = useCallback(
    (type: ExcludeInclude) => {
      const current = getValue();

      setValue(formName, { ...current, type });
    },
    [getValue, setValue]
  );
  const handleOnStateChanged = useCallback(
    (state: SingleValue<IState>) => {
      const current = getValue();

      if (!isDefined(state)) {
        if (isDefined(selectedState)) {
          delete current.filters[selectedState];
        }

        setSelectedState(undefined);
        setValue(formName, current);

        return;
      }

      const { code } = state;

      if (!isDefined(current.filters[code])) {
        setValue(formName, {
          ...current,
          filters: {
            ...current.filters,
            [code]: {
              all: (allDistricts ?? []).filter(({ state }) => state === code),
              selected: [],
            },
          },
        });
      } else {
        setValue(formName, {
          ...current,
          filters: {
            ...current.filters,
            [code]: {
              ...current.filters[code],
              all: (allDistricts ?? []).filter(({ state }) => state === code),
            },
          },
        });
      }
      setSelectedState(code);
    },
    [allDistricts, getValue, selectedState, setValue]
  );

  const placeholder = useMemo(() => {
    return isDefined(selectedState)
      ? s(['Site.All', 'Site.District'], { plural: true })
      : s(['Site.Select', 'Site.District']);
  }, [selectedState]);

  return (
    <Fragment>
      <div className="d-flex justify-content-end">
        <ExcludeIncludeButtonGroup
          onToggle={handleOnExcludeIncludeClick}
          value={getIncludeExclude()}
        />
      </div>
      <div className="mb-3">
        <Divider>{s(['Site.State'])}</Divider>
      </div>
      <VirtualSelect
        className="mb-3"
        getOptionLabel={({ name }) => name}
        getOptionValue={({ id }) => id.toString()}
        isClearable
        isDisabled={isStatesLoading}
        isLoading={isStatesLoading}
        onChange={handleOnStateChanged}
        options={states}
        placeholder={s(['Site.Select', 'Site.State'])}
        value={getStateValue()}
      />
      <div className="mb-3">
        <Divider>{s(['Site.Congressional', 'Site.District'], { plural: true })}</Divider>
      </div>
      <VirtualSelect
        closeMenuOnSelect={false}
        getOptionLabel={({ district }) => district}
        getOptionValue={({ district }) => district}
        isClearable
        isDisabled={isDistrictsLoading}
        isLoading={isDistrictsLoading}
        isMulti
        onChange={handleOnDistrictChanged}
        options={districts}
        placeholder={placeholder}
        value={getDistrictValue()}
      />
    </Fragment>
  );
};

export default Congressional;
